// Los modos que ofrece el sheet de "Generar" de mobile, para cuando NO lo monta la
// home — ADR-2026-08-04-la-barra-inferior-vive-en-el-shell.
//
// `InterfazV2` arma su lista desde `design-row`, una colección que construye en su
// propio render y que fuera de esa pantalla no existe. El shell monta la barra en
// TODAS sus pantallas, así que necesita la misma lista sin depender de la home.
//
// Se rearma desde las mismas fuentes que ya comparten `/modos` y el drawer de "¿Qué
// querés generar?" de Mi marca: `MODES` con sus feature flags, `anuncioTercero` para
// el orden, `CARRUSEL_MODOS` para las portadas y `getModePlanLock` para el candado.
// Cada modo abre por URL (`/interfaz-v2/modo/<path>`) con `presetData` en el state,
// que es el mecanismo con el que `InterfazV2` monta el sheet en el primer paint y no
// se ve un flash de la home.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MODES,
  anuncioTercero,
  carruselUltimo,
  modoDestacadoGenerar,
  modosVisibles,
  PORTADA_CUADRADA,
  portadaDeRubro,
  portadaCuadradaDeRubro,
} from './modesCatalog';
import { CARRUSEL_MODOS, HERRAMIENTAS_EXTRA } from '../estilos/contenidoAtlas';
import {
  nombreCortoModo,
  EDICIONES_RAPIDAS,
  candadoEdicionRapida,
} from '../estilos/EstiloMinimal';
import {
  getModePlanLock,
  getPlanRequirementNote,
} from '../../lib/planCapabilities';
import { getModeExamples, type ModeExample } from './modeExamples';
import { getUserPlan } from '../../lib/planUtils';
import { useUserGroup } from '../../hooks/useUserGroup';
import { esRubroHogarDeco } from '../../lib/rubro';
import type { SujetoDeGeneracion } from '../../components/SheetModosProducto';
import type { EdicionSheet, ModoSheet } from './components/SheetGenerar';

/**
 * Plantillas como una opción más del sheet —
 * ADR-2026-08-04-plantillas-es-una-opcion-de-generar.
 *
 * No es un modo de `MODES` sino un atajo al catálogo, así que se arma acá con la misma
 * entrada que ya usan la sidebar y el drawer de Mi marca (`HERRAMIENTAS_EXTRA`).
 *
 * Aparece en las DOS familias porque una plantilla se puede generar como imagen o como
 * video —es el toggle que ya vive en el preview del preset—, y desde qué familia se
 * entró es lo que decide con cuál arranca: `?media=video` viaja hasta `InterfazV2` y
 * deja el toggle en Video. Sin eso, entrar por Videos y encontrarse generando una
 * imagen sería contradecir la elección del paso anterior.
 *
 * Sólo para los rubros que ven plantillas (Moda) — el llamador lo decide con
 * `conPlantillas`, la misma bandera que usa el segundo lugar de la barra inferior.
 */
const RUTA_PLANTILLAS = '/interfaz-v2/plantillas';

/**
 * Payload del visor de ejemplos de un modo BLOQUEADO por plan —
 * ADR-2026-08-25-sheet-generar-fixes-mobile.
 *
 * Es lo que el llamador le pasa a `ModeExamplesModal` (con `actionLabel="Mejorar
 * plan"`): tocar una card con candado abre esto en vez de expulsar directo a
 * `/subscription`, que es el mismo comportamiento que la home V2 y `/modos`. El
 * modal lo renderiza el LLAMADOR y no el sheet: el tap cierra el sheet (y en Mi
 * marca lo desmonta), así que el visor tiene que vivir en alguien que siga en
 * pantalla.
 */
export interface EjemplosModoBloqueado {
  title: string;
  description?: string;
  examples: ModeExample[];
  planNote: string;
}

/**
 * La carátula de Plantillas — ADR-2026-08-04-plantillas-usa-su-caratula-de-siempre.
 *
 * Es el collage de escenas que ya muestran la card de `/modos` y el banner de la home,
 * o sea con lo que el usuario tiene asociado a Plantillas. La entrada compartida
 * (`HERRAMIENTAS_EXTRA`) apunta al hero de Anuncio, que en la grilla del sheet quedaba
 * al lado de la card de Anuncio con arte de aviso las dos.
 *
 * El archivo vive en `modo_automatico/` por historia, no porque sea de ese modo: la
 * ruta engaña, el contenido es el correcto. Se referencia acá y no se toca la tabla
 * compartida porque el cambio pedido es el de este sheet; el drawer de desktop y la
 * sidebar siguen con lo suyo.
 */
const CARATULA_PLANTILLAS = '/images/modo_automatico/modo_auto_caratula.png';

export function useModosGenerar(opciones?: {
  /**
   * Sobre qué se está generando, cuando el sheet se abre desde una grilla (un producto
   * o un modelo de Mi marca). Filtra los modos —desde un modelo, sólo los que lo
   * aceptan— y viaja al modo elegido para que abra con eso puesto.
   */
  sujeto?: SujetoDeGeneracion | null;
  /** La marca activa ve Plantillas (rubro Moda). */
  conPlantillas?: boolean;
  /**
   * Rubro de la marca activa (con el de la empresa como respaldo). Decide si entra
   * Infografía (ADR-2026-08-05-infografia-la-ve-el-rubro-hogar-y-decoracion) y qué carátula
   * muestra Catálogo (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro).
   *
   * Va como rubro crudo y no como un `conInfografia` al lado de `conPlantillas`: el
   * rubro decide DOS cosas distintas —qué modos hay y con qué foto se ven—, y un
   * booleano por consecuencia obligaría al llamador a saber todas.
   */
  rubro?: string | null;
}): {
  modos: ModoSheet[];
  ediciones: EdicionSheet[];
  /** Visor de ejemplos de un modo bloqueado (`null` = cerrado). Ver el tipo. */
  ejemplos: EjemplosModoBloqueado | null;
  cerrarEjemplos: () => void;
  /** El CTA del visor ("Mejorar plan"): la pantalla de planes, como el resto del sheet. */
  irAPlanes: () => void;
} {
  const navigate = useNavigate();
  const { isDevTester } = useUserGroup();
  const sujeto = opciones?.sujeto ?? null;
  const conPlantillas = opciones?.conPlantillas ?? false;
  const rubro = opciones?.rubro ?? null;
  // El sujeto se va en `null` al cerrar y el sheet sigue montado un rato por la
  // animación. `esModelo` sale del sujeto vivo y es de lo que depende la lista, en vez
  // del objeto entero: así no se rearma al pasar de un producto a otro.
  const esModelo = sujeto?.tipo === 'modelo';
  // Ídem — ADR-2026-08-20-desde-un-producto-tambien-hay-modos-que-no-aplican. Ojo: acá
  // el sujeto casi siempre es `null` (el sheet de la barra inferior se abre sin nada
  // elegido), y ahí `esProducto` queda en `false` y la lista NO se recorta, que es lo
  // correcto: sin producto, Animar y Carrusel sí tienen sentido.
  const esProducto = sujeto?.tipo === 'producto';

  // Plan para el gating, con el mismo arranque que `/modos` y el drawer de Mi marca:
  // el cache que llena el Sidebar —para tener candados desde el primer paint— y el
  // valor real después. Con el plan desconocido no se bloquea nada, que es lo que
  // devuelve `getModePlanLock` con `null`.
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    // El plan real, sin lentes: el panel CONTEXTO DEV y sus overrides se eliminaron
    // (supersede ADR-2026-08-03-el-plan-simulado-tambien-gatea).
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlan(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  // Visor de ejemplos del modo bloqueado que se tocó. Vive acá y no en el sheet
  // porque el tap cierra (y puede desmontar) el sheet — ver `EjemplosModoBloqueado`.
  const [ejemplos, setEjemplos] = useState<EjemplosModoBloqueado | null>(null);

  const esHogarDeco = esRubroHogarDeco(rubro);
  const modos = useMemo<ModoSheet[]>(() => {
    // Cuál va en grande arriba del sheet. Se decide ACÁ y viaja marcado en cada modo:
    // `SheetGenerar` lo tenía como un `path` fijo (`'modelo'`) y no conoce el rubro —
    // ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
    const pathDestacado = modoDestacadoGenerar(esHogarDeco);
    // Los mismos filtros que `/modos`, la home, el drawer de Mi marca y la sidebar,
    // ahora en un solo lugar —
    // ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares.
    const lista = modosVisibles(MODES, {
      isDevTester,
      esHogarDeco,
      esModelo,
      esProducto,
    }).map((m): ModoSheet => {
      const arte = CARRUSEL_MODOS.find((c) => c.id === m.path);
      // La carátula propia del rubro gana sobre la del catálogo —
      // ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro. Casi siempre es
      // `undefined` y todo sigue como antes.
      const delRubro = portadaDeRubro(m.path, rubro);
      // El lock ENTERO y no sólo el booleano: si el modo tiene ejemplos cargados, el
      // visor necesita `lock.feature` para la nota del plan.
      const lock = getModePlanLock(m.preset.kind, plan);
      const bloqueado = !!lock;
      // Una sola vez por modo: lo usan el pill "Ver ejemplos" de la card (via
      // `conEjemplos`) y el tap de acá abajo, y tienen que decir lo mismo.
      const ejemplosDelModo = getModeExamples(m.preset.kind);
      return {
        path: m.path,
        titulo: nombreCortoModo(m.path, m.preset.title),
        // La portada de la tira del inicio, con el póster de `MODES` de respaldo. Va
        // con `||` y no con `??`: el póster de Anuncio es un string VACÍO a propósito
        // —su portada es el primer cuadro del video— y acá tiene que caer al respaldo.
        imagen: delRubro || arte?.poster || m.poster || m.preset.imageUrl,
        // La 1:1, para las cards cuadradas del sheet
        // (ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile). Va aparte y no pisando
        // a `imagen` porque el sheet dibuja las dos formas: la card destacada es 3:2 —
        // la proporción de la carátula del catálogo— y la grilla es 1:1.
        // La 1:1 del rubro cuando la tiene; si no, la apaisada del rubro (que es lo que
        // devuelve `portadaCuadradaDeRubro` de respaldo) y recién después la tabla
        // genérica.
        imagenCuadrada:
          portadaCuadradaDeRubro(m.path, rubro) ?? PORTADA_CUADRADA[m.path],
        // La carátula en VIVO, para que la card 3:2 del sheet la reproduzca en loop
        // con `imagen` de póster (ADR-2026-08-25-sheet-generar-fixes-mobile). Sale del
        // catálogo del carrusel y NO de `MODES` directo, para heredar sus pisadas —
        // Catálogo va SIN video ahí a propósito. Y con carátula propia del rubro no
        // hay video: esa portada es la que se quiere ver, la misma regla que el hero
        // de la home (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro).
        video: delRubro ? undefined : arte ? arte.video : m.video,
        familia: m.preset.outputType === 'video' ? 'video' : 'imagen',
        destacado: m.path === pathDestacado,
        bloqueado,
        // El pill "Ver ejemplos" de la card bloqueada — sólo si el visor de verdad
        // va a abrir (ADR-2026-08-25-pill-ver-ejemplos-en-cards-bloqueadas).
        conEjemplos: bloqueado && ejemplosDelModo.length > 0,
        onAbrir: () => {
          // Bloqueado → la pantalla de planes, igual que una card con candado
          // (ADR-2026-08-03-modo-bloqueado-abre-pricing). Acá siempre se NAVEGA: el
          // catálogo como overlay lo provee el shell de desktop, que en mobile no está.
          if (bloqueado) {
            // Con ejemplos cargados, el visor (ejemplos + nota del plan + "Mejorar
            // plan") — lo mismo que hacen la home V2 y /modos al tocar una card con
            // candado. Sin ejemplos, directo a la pantalla de planes como antes: acá
            // no existe el FeatureLockedModal del shell.
            if (lock && ejemplosDelModo.length > 0) {
              setEjemplos({
                title: m.preset.title,
                description: m.preset.description,
                examples: ejemplosDelModo,
                planNote: getPlanRequirementNote(lock.feature),
              });
              return;
            }
            navigate('/subscription');
            return;
          }
          /* Los modos con PANTALLA PROPIA se abren por su ruta —
             ADR-2026-08-20-los-modos-con-pantalla-propia-se-abren-por-su-ruta.

             Esto era un CLICK MUERTO en producción: acá se armaba siempre
             `/interfaz-v2/modo/<path>`, pero `InterfazV2` sólo resuelve los modos que
             monta ella (su `MODE_PATH_TO_KIND` interno tiene nueve entradas escritas a
             mano y no conoce carrusel, animar-imagen ni shorts-studio). Tocar
             "Carrusel" o "Animar" en este sheet no hacía absolutamente nada.

             `m.route` es justamente el campo que el catálogo tiene para esto, y
             `/modos` ya lo respetaba. Los modos con ruta propia no reciben el `state`:
             son pantallas que arrancan eligiendo imagen y no leen `presetData`. */
          if (m.route) {
            navigate(m.route);
            return;
          }
          navigate(`/interfaz-v2/modo/${m.path}`, {
            state: {
              presetData: m.preset,
              collectionPresets: [],
              // Lo que se tocó en la grilla de Mi marca, si el sheet se abrió desde
              // ahí: el producto saltea el paso de elegirlo
              // (ADR-2026-08-04-el-producto-entra-con-el-modo) y el modelo abre el modo
              // con ese modelo ya puesto en el picker.
              productosPreseleccionados:
                sujeto?.tipo === 'producto' ? [sujeto.producto] : [],
              modeloPreseleccionado:
                sujeto?.tipo === 'modelo'
                  ? {
                      // `mode: 'preset'` = modelo guardado, la misma forma que arma el
                      // ModelPicker al elegirlo de la biblioteca.
                      mode: 'preset' as const,
                      id: String(sujeto.modelo.id),
                      name: sujeto.modelo.nombre,
                      imageUrl: sujeto.modelo.thumbnailUrl,
                    }
                  : undefined,
            },
          });
        },
      };
    });
    // Anuncio tercero, la misma posición que ocupa en la tira y en la sidebar
    // (ADR-2026-08-03-anuncio-tercero-tira-y-sidebar).
    // Y para Hogar y Decoración, Carrusel al final: en el orden natural del catálogo
    // caía PRIMERO en la familia de imágenes, siendo el modo menos importante de ese
    // rubro — ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
    const conAnuncio = anuncioTercero(lista, (m) => m.path);
    const ordenados = esHogarDeco
      ? carruselUltimo(conAnuncio, (m) => m.path)
      : conAnuncio;

    if (!conPlantillas) return ordenados;
    const plantillas = HERRAMIENTAS_EXTRA.find((h) => h.id === 'plantillas');
    if (!plantillas) return ordenados;
    /** La misma card en las dos familias; cambia con qué media abre el catálogo. */
    const cardPlantillas = (familia: 'imagen' | 'video'): ModoSheet => ({
      // `path` acá es sólo la key de la lista, así que las dos entradas necesitan una
      // distinta aunque lleven al mismo lugar.
      path: familia === 'video' ? 'plantillas-video' : 'plantillas',
      titulo: plantillas.nombre,
      imagen: CARATULA_PLANTILLAS,
      familia,
      // El catálogo se mira con cualquier plan: el candado, si toca, lo pone la
      // plantilla al generar.
      bloqueado: false,
      onAbrir: () =>
        navigate(familia === 'video' ? `${RUTA_PLANTILLAS}?media=video` : RUTA_PLANTILLAS),
    });
    return [...ordenados, cardPlantillas('imagen'), cardPlantillas('video')];
  }, [plan, isDevTester, navigate, esModelo, esProducto, sujeto, conPlantillas, rubro]);

  // Los atajos de "Ediciones rápidas": el mismo catálogo que la home y que la sección
  // de desktop, con la navegación atada acá.
  const ediciones = useMemo<EdicionSheet[]>(
    () =>
      EDICIONES_RAPIDAS.map((e) => {
        // El MISMO candado que la sección de la home, vía el helper compartido —
        // ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo. Sin un punto
        // común, el atajo de Animar frenaba en desktop y entraba en mobile.
        const lock = candadoEdicionRapida(e, plan);
        return {
          label: e.label,
          Icono: e.Icono,
          bloqueado: !!lock,
          // Bloqueado va a la pantalla de planes y no al modo. Acá se NAVEGA en vez de
          // abrir el overlay del catálogo: este sheet vive en mobile, bajo
          // `DashboardLayout`, donde el shell —y con él `abrirPricing`— no existe. Es
          // el mismo criterio que ya usa `SheetModosProducto` para sus modos
          // bloqueados.
          onAbrir: () => navigate(lock ? '/subscription' : e.ruta),
        };
      }),
    [navigate, plan],
  );

  return {
    modos,
    ediciones,
    ejemplos,
    cerrarEjemplos: () => setEjemplos(null),
    irAPlanes: () => navigate('/subscription'),
  };
}
